import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Form,
  List,
  Pagination,
  Result,
  Select,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import "remixicon/fonts/remixicon.css";
import ReactMarkdown from "react-markdown";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import request from "../utils/request";
import WidgetSessionsTab from "../components/chat/WidgetSessionsTab";
import ProjectCopilotReplay from "../componets/replay/ProjectCopilotReplay";
import WidePageHint from "../componets/common/WidePageHint";

const { Option } = Select;
const { Panel } = Collapse;
const { Text } = Typography;

interface AgentRecord {
  id: number;
  project_id?: number;
  name: string;
  agent_type?: string;
  status?: string;
}

interface ConversationItem {
  id: number;
  agentId: number;
  title: string;
  messageCount: number;
  tokenUsed: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// 工具事件：运行时随消息落库（metadata.toolEvents，内核 toolkit.Event 形状）。
interface ToolEvent {
  type: string; // tool_call | tool_result | turn_start | turn_end | checkpoint
  name?: string;
  arguments?: string;
  result?: string;
  error?: string;
  durationMs?: number;
  turn?: number;
}

interface AgentMessage {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  tokenUsed?: number;
  modelName?: string;
  metadata?: { toolEvents?: ToolEvent[] } & Record<string, unknown>;
  createdAt: string;
}

interface ChatRecord extends ConversationItem {
  projectId: number;
  projectName: string;
  agentName: string;
  messages: AgentMessage[];
}

interface ListResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

const DEFAULT_VISIBLE_MESSAGES = 3;
// 超过该条数的会话展开时启用虚拟滚动窗口
const VIRTUAL_MESSAGE_THRESHOLD = 30;
const VIRTUAL_BATCH = 20;

interface VirtualMessageListProps {
  messages: AgentMessage[];
  renderRow: (message: AgentMessage) => React.ReactNode;
}

// VirtualMessageList:在固定高度的滚动容器内按批增量渲染消息,滚动到底部哨兵时追加下一批,
// 把长会话的初始 DOM 控制在一个窗口内(无第三方依赖)。
const VirtualMessageList: React.FC<VirtualMessageListProps> = ({ messages, renderRow }) => {
  const [visibleCount, setVisibleCount] = useState(VIRTUAL_BATCH);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // 会话切换(消息数组变化)时重置窗口
  useEffect(() => {
    setVisibleCount(VIRTUAL_BATCH);
  }, [messages]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((prev) => Math.min(prev + VIRTUAL_BATCH, messages.length));
        }
      },
      { root: node.parentElement, rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [messages.length, visibleCount]);

  return (
    <div style={{ maxHeight: 600, overflowY: "auto", paddingRight: 8 }}>
      {messages.slice(0, visibleCount).map((message) => renderRow(message))}
      {visibleCount < messages.length && (
        <div ref={sentinelRef} style={{ textAlign: "center", color: "#8c8c8c", padding: 8, fontSize: 12 }}>
          正在加载更多消息…({visibleCount}/{messages.length})
        </div>
      )}
    </div>
  );
};
const CONVERSATION_PAGE_SIZE = 100;

const ChatHistory: React.FC = () => {
  const { API_BASE_URL, token, projects } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const [form] = Form.useForm();
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<number>();
  const [selectedAgentId, setSelectedAgentId] = useState<number>(0);
  const [expandedChats, setExpandedChats] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paginationRef = React.useRef(pagination);

  const hasProjects = projects.length > 0;

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const projectNameMap = useMemo(
    () => new Map(projects.map((project) => [project.id, project.name])),
    [projects]
  );

  const selectedProjectName = useMemo(() => {
    if (!selectedProject) {
      return "当前项目";
    }
    return projectNameMap.get(selectedProject) || `项目 ${selectedProject}`;
  }, [projectNameMap, selectedProject]);

  // 项目作用域：锁定到路由项目
  useEffect(() => {
    if (routeProject?.id) {
      setSelectedProject(routeProject.id);
      form.setFieldsValue({ project: routeProject.id, agent: 0 });
    } else {
      setSelectedProject(undefined);
    }
  }, [form, routeProject?.id]);

  const fetchAgents = useCallback(async () => {
    if (!token || !selectedProject) {
      setAgents([]);
      return;
    }

    setAgentsLoading(true);
    try {
      const response = await request.get<ApiResponse<ListResult<AgentRecord>>>(
        `${API_BASE_URL}/api/v3/agents`,
        {
          params: {
            page: 1,
            pageSize: 50,
            project_id: selectedProject,
          },
        }
      );

      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "获取智能体列表失败");
      }

      const nextAgents = response.data.data?.items || [];
      setAgents(nextAgents);
      setSelectedAgentId((current) => {
        if (current && nextAgents.some((agent) => agent.id === current)) {
          return current;
        }
        return 0;
      });
      form.setFieldsValue({ agent: 0 });
    } catch (error) {
      console.error("获取智能体列表失败:", error);
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [API_BASE_URL, form, selectedProject, token]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const fetchAgentConversations = useCallback(
    async (agent: AgentRecord) => {
      const response = await request.get<ApiResponse<ListResult<ConversationItem>>>(
        `${API_BASE_URL}/api/v3/agents/${agent.id}/conversations`,
        {
          params: {
            page: 1,
            pageSize: CONVERSATION_PAGE_SIZE,
          },
        }
      );

      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "获取对话列表失败");
      }

      return (response.data.data?.items || []).map((conversation) => ({
        ...conversation,
        agentName: agent.name,
        projectId: agent.project_id || selectedProject || 0,
        projectName: selectedProjectName,
      }));
    },
    [API_BASE_URL, selectedProject, selectedProjectName]
  );

  const fetchConversationMessages = useCallback(
    async (conversationId: number) => {
      const response = await request.get<ApiResponse<AgentMessage[]>>(
        `${API_BASE_URL}/api/v3/agents/conversations/${conversationId}/messages`
      );

      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "获取会话消息失败");
      }

      return response.data.data || [];
    },
    [API_BASE_URL]
  );

  const fetchChatHistory = useCallback(
    async (params?: { current?: number; pageSize?: number }) => {
      if (!token || !selectedProject) {
        return;
      }

      const currentPage = params?.current || paginationRef.current.current;
      const pageSize = params?.pageSize || paginationRef.current.pageSize;
      const targetAgents = selectedAgentId
        ? agents.filter((agent) => agent.id === selectedAgentId)
        : agents;

      if (!targetAgents.length) {
        setChatHistory([]);
        setPagination((prev) => ({ ...prev, current: currentPage, pageSize, total: 0 }));
        return;
      }

      setLoading(true);
      try {
        const conversationGroups = await Promise.all(
          targetAgents.map((agent) => fetchAgentConversations(agent))
        );
        const conversations = conversationGroups
          .flat()
          .sort(
            (left, right) =>
              new Date(right.updatedAt || right.createdAt).getTime() -
              new Date(left.updatedAt || left.createdAt).getTime()
          );
        const visibleConversations = conversations.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize
        );

        const records = await Promise.all(
          visibleConversations.map(async (conversation) => {
            try {
              const messages = await fetchConversationMessages(conversation.id);
              return { ...conversation, messages } as ChatRecord;
            } catch (error) {
              console.error("获取会话消息失败:", error);
              return { ...conversation, messages: [] } as ChatRecord;
            }
          })
        );

        setChatHistory(records);
        setExpandedChats({});
        setPagination((prev) => ({
          ...prev,
          current: currentPage,
          pageSize,
          total: conversations.length,
        }));
      } catch (error) {
        console.error("获取聊天记录失败:", error);
        setChatHistory([]);
      } finally {
        setLoading(false);
      }
    },
    [
      agents,
      fetchAgentConversations,
      fetchConversationMessages,
      selectedAgentId,
      selectedProject,
      token,
    ]
  );

  useEffect(() => {
    if (token && selectedProject && !agentsLoading) {
      fetchChatHistory({ current: 1 });
    }
  }, [agentsLoading, fetchChatHistory, selectedProject, token]);

  const handleAgentChange = (value: number) => {
    setSelectedAgentId(value);
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchChatHistory({ current: page, pageSize });
  };

  const handleSearch = () => {
    fetchChatHistory({ current: 1 });
  };

  const handleReset = () => {
    const pid = routeProject?.id;
    form.setFieldsValue({ project: pid, agent: 0 });
    setSelectedProject(pid);
    setSelectedAgentId(0);
    fetchChatHistory({ current: 1 });
  };

  const toggleExpandChat = (chatId: string) => {
    setExpandedChats((prev) => ({ ...prev, [chatId]: !prev[chatId] }));
  };

  const processMcpTags = (content: string) => {
    if (!content.includes("<MCP_HOST_TASK>")) {
      return content;
    }

    const taskRegex = /<MCP_HOST_TASK>([\s\S]*?)<\/MCP_HOST_TASK>/g;
    const resultRegex = /<MCP_HOST_RESULT>([\s\S]*?)<\/MCP_HOST_RESULT>/g;
    const taskMatches = Array.from(content.matchAll(taskRegex));
    const resultMatches = Array.from(content.matchAll(resultRegex));

    if (!taskMatches.length || taskMatches.length !== resultMatches.length) {
      return content;
    }

    let processedContent = content;
    for (let index = taskMatches.length - 1; index >= 0; index -= 1) {
      const taskMatch = taskMatches[index];
      const resultMatch = resultMatches[index];
      try {
        const taskJson = JSON.parse(taskMatch[1]);
        const toolName = taskJson.tool || "未知工具";
        const replacement = `__MCP_COLLAPSE_START__${toolName}__MCP_COLLAPSE_CONTENT__${resultMatch[1]}__MCP_COLLAPSE_END__`;
        processedContent =
          processedContent.substring(0, taskMatch.index) +
          replacement +
          processedContent.substring((resultMatch.index || 0) + resultMatch[0].length);
      } catch (error) {
        console.error("处理MCP标签时出错:", error);
      }
    }

    return processedContent;
  };

  const renderWithCollapse = (content: string) => {
    if (!content.includes("__MCP_COLLAPSE_START__")) {
      return <ReactMarkdown>{content}</ReactMarkdown>;
    }

    const parts = content.split("__MCP_COLLAPSE_START__");
    const result: React.ReactNode[] = [];
    if (parts[0]) {
      result.push(<ReactMarkdown key="prefix">{parts[0]}</ReactMarkdown>);
    }

    parts.slice(1).forEach((part, index) => {
      const [title, rest] = part.split("__MCP_COLLAPSE_CONTENT__");
      if (!rest) {
        return;
      }
      const [collapseContent, remainingContent] = rest.split("__MCP_COLLAPSE_END__");
      result.push(
        <Collapse key={`collapse-${index}`} style={{ margin: "12px 0" }}>
          <Panel header={`工具调用: ${title}`} key="1">
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {collapseContent.trim()}
            </pre>
          </Panel>
        </Collapse>
      );
      if (remainingContent) {
        result.push(<ReactMarkdown key={`remaining-${index}`}>{remainingContent}</ReactMarkdown>);
      }
    });

    return result;
  };

  // 结构化工具轨迹：优先使用 metadata.toolEvents（运行时落库的真实轨迹），
  // 旧消息无 metadata 时回落到正文正则解析（processMcpTags 兼容路径）。
  const renderToolEvents = (events: ToolEvent[]) => {
    // tool_call 与 tool_result 按 name+顺序配对合并成一张卡。
    const calls: Array<{ name: string; arguments?: string; result?: string; error?: string; durationMs?: number; turn?: number }> = [];
    for (const event of events) {
      if (event.type === "tool_call") {
        calls.push({ name: event.name || "未知工具", arguments: event.arguments, turn: event.turn });
      } else if (event.type === "tool_result") {
        const open = [...calls].reverse().find((c) => c.name === (event.name || "未知工具") && c.result === undefined && c.error === undefined);
        if (open) {
          open.result = event.result;
          open.error = event.error;
          open.durationMs = event.durationMs;
        } else {
          calls.push({ name: event.name || "未知工具", result: event.result, error: event.error, durationMs: event.durationMs, turn: event.turn });
        }
      }
    }
    if (!calls.length) return null;
    return (
      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        items={calls.map((call, index) => ({
          key: String(index),
          label: (
            <Space wrap>
              <Tag color={call.error ? "error" : "cyan"} icon={<i className="ri-tools-line" />}>{call.name}</Tag>
              {typeof call.turn === "number" && call.turn > 0 && <Tag>第 {call.turn} 轮</Tag>}
              {typeof call.durationMs === "number" && call.durationMs > 0 && <Tag>{call.durationMs}ms</Tag>}
              <Tag color={call.error ? "error" : "success"}>{call.error ? "失败" : "完成"}</Tag>
            </Space>
          ),
          children: (
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {call.arguments && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>输入</Text>
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", margin: "4px 0 0", fontSize: 12 }}>{call.arguments.slice(0, 1000)}</pre>
                </div>
              )}
              {call.result && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>结果</Text>
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", margin: "4px 0 0", fontSize: 12 }}>{call.result.slice(0, 2000)}</pre>
                </div>
              )}
              {call.error && <Text type="danger">{call.error}</Text>}
            </Space>
          ),
        }))}
      />
    );
  };

  const renderMessage = (message: AgentMessage) => {
    if (message.role !== "assistant") {
      return <div style={{ whiteSpace: "pre-wrap" }}>{message.content}</div>;
    }

    const toolEvents = Array.isArray(message.metadata?.toolEvents) ? message.metadata!.toolEvents! : [];
    const processedContent = processMcpTags(message.content);
    return (
      <div className="markdown-content" style={{ whiteSpace: "pre-wrap" }}>
        {renderToolEvents(toolEvents)}
        {renderWithCollapse(processedContent)}
      </div>
    );
  };

  const renderMessageRow = (message: AgentMessage, agentName: string) => {
    const isUser = message.role === "user";
    return (
      <div
        key={message.id}
        style={{
          margin: "16px 0",
          display: "flex",
          flexDirection: isUser ? "row-reverse" : "row",
          alignItems: "flex-start",
        }}
      >
        <Avatar
          style={{
            backgroundColor: isUser ? "#1890ff" : "#52c41a",
            marginRight: isUser ? 0 : 12,
            marginLeft: isUser ? 12 : 0,
            flexShrink: 0,
          }}
          icon={isUser ? <i className="ri-user-line" /> : <i className="ri-bard-line" />}
        />
        <div style={{ maxWidth: "80%" }}>
          <div
            style={{
              fontSize: 12,
              color: "#8c8c8c",
              marginBottom: 4,
              textAlign: isUser ? "right" : "left",
            }}
          >
            {isUser ? "用户" : agentName}
            {message.modelName ? ` · ${message.modelName}` : ""}
          </div>
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 8,
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              backgroundColor: isUser ? "#E6F7FF" : "#F6FFED",
              border: isUser ? "1px solid #BAE7FF" : "1px solid #D9F7BE",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {renderMessage(message)}
          </div>
        </div>
      </div>
    );
  };

  const renderMessages = (item: ChatRecord) => {
    const chatId = String(item.id);
    const isExpanded = expandedChats[chatId] || false;
    const visibleMessages = isExpanded
      ? item.messages
      : item.messages.slice(0, DEFAULT_VISIBLE_MESSAGES);
    const hasMoreMessages = item.messages.length > DEFAULT_VISIBLE_MESSAGES;

    // 长会话展开后用虚拟滚动窗口,避免一次性渲染数百条消息拖垮浏览器。
    if (isExpanded && item.messages.length > VIRTUAL_MESSAGE_THRESHOLD) {
      return (
        <div style={{ padding: "8px 0" }}>
          <VirtualMessageList
            messages={item.messages}
            renderRow={(message) => renderMessageRow(message, item.agentName)}
          />
          <Divider plain>
            <Button
              type="link"
              onClick={() => toggleExpandChat(chatId)}
              icon={<i className="ri-arrow-up-s-line" />}
            >
              收起消息
            </Button>
          </Divider>
        </div>
      );
    }

    return (
      <div style={{ padding: "8px 0" }}>
        {visibleMessages.map((message) => renderMessageRow(message, item.agentName))}

        {hasMoreMessages && (
          <Divider plain>
            <Button
              type="link"
              onClick={() => toggleExpandChat(chatId)}
              icon={<i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />}
            >
              {isExpanded
                ? "收起消息"
                : `展开剩余 ${item.messages.length - DEFAULT_VISIBLE_MESSAGES} 条消息`}
            </Button>
          </Divider>
        )}
      </div>
    );
  };

  if (!hasProjects) {
    return (
      <div style={{ background: "#f0f2f5", minHeight: "100%", borderRadius: 8, padding: 16 }}>
        <Result
          status="warning"
          title="暂无项目"
          subTitle="创建项目后即可按项目和智能体查看聊天记录。"
          extra={
            <Button type="primary" href="/console" icon={<i className="ri-add-line" style={{ marginRight: 8 }} />}>
              返回首页
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", borderRadius: 8, padding: 16 }}>
      <WidePageHint message="聊天记录在桌面端查看长会话与工具轨迹体验更佳。" />
      <Card
        title={
          <Space>
            <i className="ri-message-3-line" style={{ color: "#1677ff" }} />
            <span style={{ fontWeight: "bold" }}>聊天记录</span>
          </Space>
        }
      >
        <Tabs
          defaultActiveKey="agent"
          items={[
            {
              key: "agent",
              label: "Agent 对话",
              children: (
                <>
        <Form form={form} layout="inline" style={{ marginBottom: 24 }} onFinish={handleSearch}>
          <Form.Item label="项目">
            <Tag color="blue" style={{ height: 32, lineHeight: "30px", padding: "0 12px" }}>
              {routeProject?.name || "—"}
            </Tag>
          </Form.Item>
          <Form.Item name="agent" label="智能体">
            <Select
              style={{ width: 220 }}
              value={selectedAgentId}
              loading={agentsLoading}
              onChange={handleAgentChange}
              placeholder="选择智能体"
            >
              <Option value={0}>全部智能体</Option>
              {agents.map((agent) => (
                <Option key={agent.id} value={agent.id}>
                  {agent.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {chatHistory.length === 0 && !loading ? (
          <Empty description={agents.length ? "暂无聊天记录" : "当前项目暂无智能体"} />
        ) : (
          <List
            loading={loading}
            dataSource={chatHistory}
            renderItem={(item) => (
              <List.Item>
                <Card
                  type="inner"
                  style={{ width: "100%" }}
                  styles={{ body: { padding: 16 } }}
                  title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Space wrap>
                        <Tooltip title={`项目ID: ${item.projectId}`}>
                          <Tag icon={<i className="ri-folder-3-line" />} color="blue">
                            {item.projectName}
                          </Tag>
                        </Tooltip>
                        <Tooltip title={`Agent ID: ${item.agentId}`}>
                          <Tag icon={<i className="ri-robot-2-line" />} color="cyan">
                            {item.agentName}
                          </Tag>
                        </Tooltip>
                        <Tooltip title={item.title}>
                          <Tag color="purple">{item.title || `会话 ${item.id}`}</Tag>
                        </Tooltip>
                        <Tag icon={<i className="ri-chat-check-line" />} color="success">
                          {item.messages.length} 条消息
                        </Tag>
                        {item.tokenUsed > 0 && <Tag color="gold">{item.tokenUsed} Token</Tag>}
                      </Space>
                      <Button
                        type="text"
                        icon={<i className="ri-file-copy-line" />}
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(item.messages, null, 2));
                        }}
                      >
                        复制
                      </Button>
                    </div>
                  }
                >
                  {item.messages.length ? renderMessages(item) : <Text type="secondary">此会话暂无消息内容</Text>}
                </Card>
              </List.Item>
            )}
          />
        )}

        <div style={{ textAlign: "right", marginTop: 20 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条记录`}
          />
        </div>
                </>
              ),
            },
            {
              key: "chat",
              label: "对话/挂件会话",
              children: <WidgetSessionsTab />,
            },
            {
              key: "replay",
              label: "Copilot 回放",
              children: <ProjectCopilotReplay />,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ChatHistory;
